import React, { useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useAuthentication } from "../context/authentication";
import { useBillingPlans } from "../context/billingPlans/BillingPlansProvider";
import { DottedMenu } from "../context/dropdownMenu/DropdownMenuProvider";
import { useInstalls } from "../context/installs";
import { useLocations } from "../context/locations";
import { useToast } from "../context/toast";
import { useTransmitScales } from "../context/transmitScales";
import { useWorkspaces } from "../context/workspaces/WorkspacesProvider";
import { HotspotLocation, Install, PlanType } from "../utils/types";
import useTransmitScaleConfig from "../utils/useTransmitScaleConfig";

interface Props {
  disabled?: boolean;
  location: HotspotLocation;
}

const LocationActionsButton: React.FC<Props> = ({ disabled, location }) => {
  const { setModalWarningConfig } = useToast();
  const { wrapActionWithAuth } = useAuthentication();
  const { currentWorkspace } = useWorkspaces();
  const { setStartPointLosMapConfig } = useLocations();
  const { removeLocations, setEditLocationConfig } = useLocations();
  const { toggleInstallSidebar } = useInstalls();
  const { getPlanByType, setNewPlan } = useBillingPlans();
  const browserLocation = useLocation();
  const { setModalUpgradeConfig } = useToast();
  const history = useHistory();
  const { isTransmitScaleActive } = useTransmitScales();
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(browserLocation.pathname, {
    path: "/locations/:id/:section"
  });

  const emptyInstall: Install = useMemo(
    () => ({
      uuid: "",
      created_by: "",
      created_at: "",
      updated_by: "",
      updated_at: "",
      hotspot_uuid: "",
      location_uuid: location.uuid,
      installed_at: "",
      uninstalled_at: "",
      commissions: []
    }),
    [location.uuid]
  );

  return (
    <DottedMenu
      disabled={disabled}
      text="Open location actions"
      className="z-10"
      menuItems={[
        {
          text: "Add install",
          onClick: wrapActionWithAuth(() => toggleInstallSidebar(emptyInstall))
        },
        {
          text: "Edit location",
          onClick: wrapActionWithAuth(() => {
            setEditLocationConfig({ location, show: true });
          })
        },
        {
          text: `${
            isTransmitScaleActive(location.uuid) ? "Disable " : "Enable "
          } transmit scale`,
          onClick: () => {
            toggleIsActive(location.uuid);
          }
        },
        {
          text: "Remove location",
          onClick: wrapActionWithAuth(() => {
            setModalWarningConfig({
              open: true,
              resolve: () => {
                if (isTransmitScaleActive(location.uuid)) {
                  toggleIsActive(location.uuid);
                }
                removeLocations([location.uuid]);

                if (detailsPageMatch?.params?.id === location.uuid) {
                  history.push("/locations");
                }
              }
            });
          })
        },
        {
          text: "Line of sight",
          onClick: wrapActionWithAuth(() => {
            if (!currentWorkspace?.permissions["line-of-sight-tool"]) {
              setModalUpgradeConfig({
                open: true,
                resolve: () => {
                  setNewPlan((prevState) => ({
                    ...prevState,
                    plan: getPlanByType(PlanType.pro)
                  }));
                },
                nextPlan: getPlanByType(PlanType.pro),
                featureTitle: "Line of sight",
                featureDescription:
                  "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
              });
              return;
            }

            history.push("/locations");
            setStartPointLosMapConfig(
              location.lat,
              location.lng,
              location.height
            );
          })
        }
      ]}
    />
  );
};

export default LocationActionsButton;
